import { useState } from "react";
import { Link, createFileRoute, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";
import PasswordInput from "@/components/password-input";
import { useForgotPasswordCompleteMutation } from "@/queries/hooks/authHooks";

export const Route = createFileRoute("/auth/_layout/password-reset")({
  component: PasswordResetPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || "",
  }),
});

function PasswordResetPage() {
  const { t } = useTranslation("auth");
  const { token } = useSearch({ from: "/auth/_layout/password-reset" });
  const resetMutation = useForgotPasswordCompleteMutation();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <>
        <h1 className="heading-1 mb-5">{t("passwordReset.invalidLinkTitle")}</h1>
        <p className="body mb-6">{t("passwordReset.invalidLinkBody")}</p>
        <Button variant="secondary" className="mt-8 w-[320px]" asChild>
          <Link to="/auth/forgot-password">
            {t("passwordReset.requestNewLink")}
          </Link>
        </Button>
      </>
    );
  }

  if (success) {
    return (
      <>
        <h1 className="heading-1 mb-5">{t("passwordReset.successTitle")}</h1>
        <img src="/checkmark-2.svg" alt="Success" width={60} height={60} className="mb-6" />
        <p className="body mb-6">{t("passwordReset.successBody")}</p>
        <Button variant="secondary" className="mt-8 w-[320px]" asChild>
          <Link to="/auth/login">{t("passwordReset.logIn")}</Link>
        </Button>
      </>
    );
  }

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) return t("passwordReset.passwordTooShort");
    if (!/[A-Z]/.test(pw)) return t("passwordReset.passwordNeedsUppercase");
    if (!/[a-z]/.test(pw)) return t("passwordReset.passwordNeedsLowercase");
    if (!/[0-9]/.test(pw)) return t("passwordReset.passwordNeedsNumber");
    if (!/[@$!%*?&]/.test(pw)) return t("passwordReset.passwordNeedsSpecial");
    return null;
  };

  const handleSubmit = async () => {
    setErrorMsg(null);

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg(t("passwordReset.passwordsDoNotMatch"));
      return;
    }

    try {
      await resetMutation.mutateAsync({ token, newPassword });
      setSuccess(true);
    } catch (err: unknown) {
      setErrorMsg(
        err instanceof Error ? err.message : t("passwordReset.resetError"),
      );
    }
  };

  return (
    <>
      <h1 className="heading-1 mb-5">{t("passwordReset.setNewPasswordTitle")}</h1>
      <p className="body mb-6">{t("passwordReset.setNewPasswordPrompt")}</p>
      <div className="flex flex-col gap-4 w-full max-w-[320px]">
        <PasswordInput
          label={t("passwordReset.newPasswordLabel")}
          placeholder={t("passwordReset.passwordPlaceholder")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <PasswordInput
          label={t("passwordReset.repeatPasswordLabel")}
          placeholder={t("passwordReset.passwordPlaceholder")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
        />
      </div>
      {errorMsg && <p className="body-small text-red-500 mt-2">{errorMsg}</p>}
      <div className="border-[#200A0A] w-full border mt-3" />
      <Button
        variant="secondary"
        className="mt-16 w-[320px]"
        onClick={handleSubmit}
        disabled={resetMutation.isPending}
      >
        {resetMutation.isPending
          ? t("passwordReset.submitting")
          : t("passwordReset.submit")}
      </Button>
    </>
  );
}
